import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { blogService } from '../../services/blog.service';
import { FaCheck, FaTimes, FaEye, FaGlobe, FaLock, FaTimes as FaClose } from 'react-icons/fa';

const BlogApprovals = () => {
    const [pendingBlogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [action, setAction] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [makePublic, setMakePublic] = useState(false);

    useEffect(() => {
        fetchPendingBlogs();
    }, []);

    const fetchPendingBlogs = async () => {
        try {
            setLoading(true);
            const data = await blogService.getAllBlogs({ status: 'pending' });
            setBlogs(data.blogs || []);
        } catch (error) {
            toast.error('Failed to fetch pending blogs');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        try {
            await blogService.approveBlog(selectedBlog._id, makePublic);
            toast.success('Blog approved successfully!');
            setShowModal(false);
            setSelectedBlog(null);
            setMakePublic(false);
            fetchPendingBlogs();
        } catch (error) {
            toast.error('Failed to approve blog');
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        try {
            await blogService.rejectBlog(selectedBlog._id, rejectionReason);
            toast.success('Blog rejected');
            setShowModal(false);
            setSelectedBlog(null);
            setRejectionReason('');
            fetchPendingBlogs();
        } catch (error) {
            toast.error('Failed to reject blog');
        }
    };

    const openModal = (blog, actionType) => {
        setSelectedBlog(blog);
        setAction(actionType);
        setShowModal(true);
    };

    const openPreview = (blog) => {
        setSelectedBlog(blog);
        setShowPreviewModal(true);
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Blog Approvals</h1>
                <p className="text-gray-600 mt-2">Review and approve pending blog posts</p>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-gray-500 text-sm">Pending Approval</p>
                        <p className="text-3xl font-bold text-yellow-600">{pendingBlogs.length}</p>
                    </div>
                    <div className="text-yellow-500 text-4xl">
                        <FaEye />
                    </div>
                </div>
            </div>

            {/* Pending Blogs */}
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            ) : pendingBlogs.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <p className="text-gray-500 text-lg">No pending blogs to review</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {pendingBlogs.map((blog) => (
                        <div key={blog._id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold mb-2">{blog.title}</h3>
                                        <p className="text-sm text-gray-500">
                                            By <strong>{blog.authorName}</strong> •
                                            Submitted on {new Date(blog.submittedAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                                        Pending Review
                                    </span>
                                </div>

                                {/* Featured Image */}
                                {blog.featuredImage && (
                                    <img
                                        src={blog.featuredImage}
                                        alt={blog.title}
                                        className="w-full h-64 object-cover rounded-lg mb-4"
                                    />
                                )}

                                {/* Excerpt */}
                                <p className="text-gray-700 mb-4">{blog.excerpt}</p>

                                {/* Meta Info */}
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded capitalize">
                                        {blog.category}
                                    </span>
                                    {blog.tags?.slice(0, 3).map((tag, index) => (
                                        <span key={index} className="bg-gray-100 px-3 py-1 rounded">
                                            #{tag}
                                        </span>
                                    ))}
                                    <span>{blog.readTime} min read</span>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-4 pt-4 border-t">
                                    <button
                                        onClick={() => openPreview(blog)}
                                        className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2"
                                    >
                                        <FaEye /> Preview Full Content
                                    </button>
                                    <button
                                        onClick={() => openModal(blog, 'approve')}
                                        className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                                    >
                                        <FaCheck /> Approve
                                    </button>
                                    <button
                                        onClick={() => openModal(blog, 'reject')}
                                        className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                                    >
                                        <FaTimes /> Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Preview Modal */}
            {showPreviewModal && selectedBlog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                            <h2 className="text-2xl font-bold">Preview: {selectedBlog.title}</h2>
                            <button
                                onClick={() => setShowPreviewModal(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                <FaClose />
                            </button>
                        </div>

                        <div className="p-8">
                            {/* Featured Image */}
                            {selectedBlog.featuredImage && (
                                <img
                                    src={selectedBlog.featuredImage}
                                    alt={selectedBlog.title}
                                    className="w-full h-96 object-cover rounded-lg mb-6"
                                />
                            )}

                            {/* Meta */}
                            <div className="mb-6">
                                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm capitalize mr-2">
                                    {selectedBlog.category}
                                </span>
                                <span className="text-sm text-gray-500">
                                    By {selectedBlog.authorName} • {new Date(selectedBlog.createdAt).toLocaleDateString()}
                                </span>
                            </div>

                            {/* Title */}
                            <h1 className="text-4xl font-bold mb-4">{selectedBlog.title}</h1>

                            {/* Content */}
                            <div className="prose max-w-none">
                                <div className="text-lg leading-relaxed whitespace-pre-wrap">
                                    {selectedBlog.content}
                                </div>
                            </div>

                            {/* Tags */}
                            {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                                <div className="mt-8 pt-8 border-t">
                                    <div className="flex flex-wrap gap-2">
                                        {selectedBlog.tags.map((tag, index) => (
                                            <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="mt-8 pt-8 border-t flex gap-4">
                                <button
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        openModal(selectedBlog, 'approve');
                                    }}
                                    className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                                >
                                    <FaCheck /> Approve This Blog
                                </button>
                                <button
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        openModal(selectedBlog, 'reject');
                                    }}
                                    className="flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                                >
                                    <FaTimes /> Reject This Blog
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval/Rejection Modal */}
            {showModal && selectedBlog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        {action === 'approve' ? (
                            <>
                                <h2 className="text-2xl font-bold mb-4">Approve Blog</h2>
                                <p className="text-gray-600 mb-4">
                                    You're about to approve: <strong>{selectedBlog.title}</strong>
                                </p>

                                <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                                    <p className="text-sm text-blue-800 mb-3">
                                        <strong>What happens next?</strong>
                                    </p>
                                    <ul className="text-sm text-blue-700 space-y-1">
                                        <li>• Blog status changes to "Approved"</li>
                                        <li>• Author will be notified</li>
                                        <li>• You can publish it later</li>
                                    </ul>
                                </div>

                                <div className="mb-6">
                                    <label className="flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="checkbox"
                                            checked={makePublic}
                                            onChange={(e) => setMakePublic(e.target.checked)}
                                            className="w-5 h-5"
                                        />
                                        <div className="flex items-center gap-2">
                                            {makePublic ? <FaGlobe className="text-green-600" /> : <FaLock className="text-gray-400" />}
                                            <div>
                                                <p className="font-medium">Make this blog public</p>
                                                <p className="text-xs text-gray-500">
                                                    {makePublic ? 'Visible without login' : 'Internal only'}
                                                </p>
                                            </div>
                                        </div>
                                    </label>
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handleApprove}
                                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowModal(false);
                                            setSelectedBlog(null);
                                            setMakePublic(false);
                                        }}
                                        className="flex-1 bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="text-2xl font-bold mb-4">Reject Blog</h2>
                                <p className="text-gray-600 mb-4">
                                    Rejecting: <strong>{selectedBlog.title}</strong>
                                </p>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2">
                                        Reason for Rejection *
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500"
                                        rows="4"
                                        placeholder="Please provide clear feedback so the author can improve..."
                                        required
                                    ></textarea>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Be specific about what needs to be improved
                                    </p>
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handleReject}
                                        className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                                    >
                                        Reject Blog
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowModal(false);
                                            setSelectedBlog(null);
                                            setRejectionReason('');
                                        }}
                                        className="flex-1 bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogApprovals;